import { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';
import TimerModal from './TimerModal';
import { PRESET_ROUTINES } from './routinePresets';

const SECTION_ICONS = { workout: '⚡', meditation: '🧘', flexibility: '🤸', generic: '⏱', custom: '✦', breathing: '🌬️' };
const SECTION_COLORS = {
  workout: { bg: '#ff4d1c', light: '#fff1ee' },
  meditation: { bg: '#3d5af1', light: '#eef0ff' },
  flexibility: { bg: '#9b59b6', light: '#f5eeff' },
  generic: { bg: '#555e6e', light: '#f0f1f3' },
  custom: { bg: '#00b87a', light: '#e6fff5' },
  breathing: { bg: '#00b4d8', light: '#e0f7fc' },
};
const today = () => new Date().toISOString().slice(0, 10);
const yesterday = () => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); };

// ── Routine Card ──────────────────────────────────────────────────────────────
function RoutineCard({ section, logs, onDelete, onTimer }) {
  const color = SECTION_COLORS[section.type] || SECTION_COLORS.custom;
  const todayLog = logs[today()] || {};
  const presetMatch = (PRESET_ROUTINES[section.type] || []).find(p => p.name === section.name);
  const matchedPreset = section.subroutines
    ? { name: section.name, subroutines: section.subroutines }
    : presetMatch || null;
  const description = section.goal || presetMatch?.description || null;

  // Calculate streak from logs
  const streak = (() => {
    let s = 0, d = new Date();
    while (true) {
      const key = d.toISOString().slice(0, 10);
      if (logs[key]?.done) { s++; d.setDate(d.getDate() - 1); } else break;
    }
    return s;
  })();

  const history = Object.entries(logs).sort((a, b) => b[0].localeCompare(a[0])).slice(0, 7);

  return (
    <div style={{ ...s.card, borderLeft: `5px solid ${color.bg}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 22 }}>{SECTION_ICONS[section.type]}</span>
            <span style={{ fontWeight: 700, fontSize: 17, color: '#111' }}>{section.name}</span>
            <span style={{ ...s.badge, background: color.light, color: color.bg }}>{section.type}</span>
          </div>
          {description && <p style={{ fontSize: 13, color: '#777', marginTop: 4 }}>{description}</p>}
          {section.subroutines?.length > 0 && (
            section.subroutines[0]?.breathing ? (
              <div style={{ marginTop: 6 }}>
                {(() => {
                  const b = section.subroutines[0];
                  return (
                    <span style={{ fontSize: 12, color: '#999' }}>
                      Inhale {b.inhale}
                      {b.hold > 0 ? ` · Hold ${b.hold}` : ''}
                      {` · Exhale ${b.exhale}`}
                      {b.hold2 > 0 ? ` · Hold ${b.hold2}` : ''}
                    </span>
                  );
                })()}
              </div>
            ) : (
              <div style={{ marginTop: 6 }}>
                {section.subroutines.map((sr, i) => (
                  <span key={i} style={{ fontSize: 12, color: '#999', marginRight: 8 }}>
                    {sr.name} · {sr.duration ? `${sr.duration / 60}min` : 'open'}
                    {sr.delay > 0 ? ` · ${sr.delay}s delay` : ''}
                  </span>
                ))}
              </div>
            )
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button onClick={() => onTimer(section, matchedPreset)} style={{ ...s.startBtn, background: color.bg }}>▶ Start</button>
          <button onClick={() => onDelete(section.id)} style={{ ...s.iconBtn, background: '#fff0ee', color: '#cc2200' }}>✕</button>
        </div>
      </div>

      {(todayLog.done || streak > 0) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
          {todayLog.done && <span style={{ fontSize: 12, fontWeight: 600, color: color.bg, background: color.light, padding: '3px 10px', borderRadius: 20 }}>✓ Done today</span>}
          {streak > 0 && <span style={{ fontSize: 13, color: '#888' }}>🔥 {streak} day streak</span>}
        </div>
      )}

    </div>
  );
}

// ── New Routine Form ──────────────────────────────────────────────────────────
function NewRoutineForm({ onAdd, onCancel }) {
  const [mode, setMode] = useState('pick'); // 'pick' | 'custom' | 'breathing'
  const [selectedType, setSelectedType] = useState(null);
  const [name, setName] = useState('');
  const [type] = useState('custom');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [subs, setSubs] = useState([{ name: '', durationMin: '', delaySec: '0', openEnded: false, intervalMin: '' }]);

  // Breathing form state
  const [breathingName, setBreathingName] = useState('');
  const [breathingInhale, setBreathingInhale] = useState('');
  const [breathingHold, setBreathingHold] = useState('0');
  const [breathingExhale, setBreathingExhale] = useState('');
  const [breathingHold2, setBreathingHold2] = useState('0');
  const [breathingDuration, setBreathingDuration] = useState('');

  const selectPreset = async (presetName, presetType, description) => {
    setLoading(true);
    await onAdd({ name: presetName, type: presetType, description: description || null, subroutines: null });
    setLoading(false);
  };

  const selectBreathingPreset = (preset) => {
    setBreathingName(preset.name);
    setBreathingInhale(String(preset.inhale));
    setBreathingHold(String(preset.hold ?? 0));
    setBreathingExhale(String(preset.exhale));
    setBreathingHold2(String(preset.hold2 ?? 0));
    setBreathingDuration('');
    setMode('breathing');
  };

  const submitBreathing = async () => {
    const inhale = parseFloat(breathingInhale);
    const hold = parseFloat(breathingHold) || 0;
    const exhale = parseFloat(breathingExhale);
    const hold2 = parseFloat(breathingHold2) || 0;
    const duration = parseFloat(breathingDuration) * 60;
    if (!inhale || inhale <= 0) return setErr('Inhale duration is required');
    if (!exhale || exhale <= 0) return setErr('Exhale duration is required');
    if (!duration || duration <= 0) return setErr('Total duration is required');
    if (!breathingName.trim()) return setErr('Routine name is required');
    setErr('');
    setLoading(true);
    const subroutines = [{ breathing: true, inhale, hold, exhale, hold2, totalDuration: duration }];
    const error = await onAdd({ name: breathingName.trim(), type: 'breathing', description: null, subroutines });
    setLoading(false);
    if (error) setErr(error.message || 'Failed to save');
  };

  const updateSub = (i, field, val) =>
    setSubs(prev => prev.map((sr, idx) => idx === i ? { ...sr, [field]: val } : sr));

  const addSub = () => setSubs(prev => [...prev, { name: '', durationMin: '', delaySec: '0', openEnded: false, intervalMin: '' }]);

  const removeSub = (i) => setSubs(prev => prev.filter((_, idx) => idx !== i));

  const submitCustom = async () => {
    if (!name.trim()) return setErr('Please enter a routine name.');
    setErr('');
    setLoading(true);
    const subroutines = subs
      .filter(sr => sr.name.trim())
      .map((sr, i) => ({
        name: sr.name.trim(),
        duration: sr.openEnded ? null : (sr.durationMin !== '' ? Math.round(parseFloat(sr.durationMin) * 60) : null),
        interval: sr.openEnded && sr.intervalMin !== '' ? Math.round(parseFloat(sr.intervalMin) * 60) : null,
        delay: i === 0 ? 0 : (parseInt(sr.delaySec) || 0),
      }));
    const error = await onAdd({ name: name.trim(), type, subroutines: subroutines.length > 0 ? subroutines : null });
    setLoading(false);
    if (error) setErr(error.message || 'Failed to save. Please try again.');
  };

  if (mode === 'breathing') {
    const bColor = SECTION_COLORS.breathing;
    return (
      <div style={s.formCard}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <button onClick={() => { setMode(selectedType ? 'category' : 'pick'); }} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#888' }}>←</button>
          <h3 style={{ fontWeight: 700, fontSize: 16 }}>🌬️ Breathing Routine</h3>
        </div>
        {err && <p style={{ fontSize: 13, color: '#cc2200', background: '#fff0ee', borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>{err}</p>}
        <input value={breathingName} onChange={e => setBreathingName(e.target.value)} placeholder="Routine name" style={s.input} />
        <p style={{ ...s.label, margin: '4px 0 8px' }}>Cycle Pattern</p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          {[
            { label: 'INHALE', color: bColor.bg, val: breathingInhale, set: setBreathingInhale },
            { label: 'HOLD', color: '#9b59b6', val: breathingHold, set: setBreathingHold },
            { label: 'EXHALE', color: '#00b87a', val: breathingExhale, set: setBreathingExhale },
            { label: 'HOLD 2', color: '#f39c12', val: breathingHold2, set: setBreathingHold2 },
          ].map(({ label, color, val, set }) => (
            <div key={label} style={{ flex: 1 }}>
              <p style={{ fontSize: 11, color, fontWeight: 600, marginBottom: 4 }}>{label}</p>
              <input type="number" min="0" step="any" value={val} onChange={e => set(e.target.value)}
                placeholder="0" style={{ ...s.input, marginBottom: 0 }} />
            </div>
          ))}
        </div>
        <p style={{ ...s.label, margin: '8px 0 6px' }}>Total Duration</p>
        <input type="number" min="0" step="any" value={breathingDuration} onChange={e => setBreathingDuration(e.target.value)}
          placeholder="Duration (minutes)" style={s.input} />
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={submitBreathing} disabled={loading}
            style={{ ...s.actionBtn, flex: 1, background: bColor.bg, opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Saving…' : 'Add Routine'}
          </button>
          <button onClick={onCancel} style={{ ...s.actionBtn, flex: 1, background: '#eee', color: '#333' }}>Cancel</button>
        </div>
      </div>
    );
  }

  if (mode === 'custom') {
    return (
      <div style={s.formCard}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <button onClick={() => setMode('pick')} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#888' }}>←</button>
          <h3 style={{ fontWeight: 700, fontSize: 16 }}>Custom Routine</h3>
        </div>

        <input value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Routine name (e.g. Morning Lift)" style={s.input} />

        <p style={{ ...s.label, margin: '4px 0 10px' }}>Subroutines</p>
        {subs.map((sr, i) => (
          <div key={i} style={{ background: '#f7f7f7', borderRadius: 10, padding: '10px 12px', marginBottom: 8 }}>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
              <input value={sr.name} onChange={(e) => updateSub(i, 'name', e.target.value)}
                placeholder="Name (e.g. Warm Up)"
                style={{ ...s.input, marginBottom: 0, flex: 1, fontSize: 13 }} />
              {subs.length > 1 && (
                <button onClick={() => removeSub(i)} style={{ background: 'none', border: 'none', color: '#cc2200', fontSize: 16, cursor: 'pointer', lineHeight: 1 }}>✕</button>
              )}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#888', marginBottom: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={sr.openEnded} onChange={(e) => updateSub(i, 'openEnded', e.target.checked)} />
              Open ended
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 11, color: '#aaa', marginBottom: 4 }}>{sr.openEnded ? 'Interval (min)' : 'Duration (min)'}</p>
                {sr.openEnded ? (
                  <input type="number" min="0" step="0.5" value={sr.intervalMin}
                    onChange={(e) => updateSub(i, 'intervalMin', e.target.value)}
                    placeholder="Bell every X min"
                    style={{ ...s.input, marginBottom: 0, fontSize: 13 }} />
                ) : (
                  <input type="number" min="0" step="0.5" value={sr.durationMin}
                    onChange={(e) => updateSub(i, 'durationMin', e.target.value)}
                    placeholder="e.g. 5"
                    style={{ ...s.input, marginBottom: 0, fontSize: 13 }} />
                )}
              </div>
              {i > 0 && (
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 11, color: '#aaa', marginBottom: 4 }}>Delay (sec)</p>
                  <input type="number" min="0" value={sr.delaySec}
                    onChange={(e) => updateSub(i, 'delaySec', e.target.value)}
                    placeholder="0"
                    style={{ ...s.input, marginBottom: 0, fontSize: 13 }} />
                </div>
              )}
            </div>
          </div>
        ))}

        <button onClick={addSub} style={{ ...s.actionBtn, width: '100%', background: '#f0f0f0', color: '#555', marginBottom: 12 }}>
          + Add Subroutine
        </button>

        {err && <p style={{ fontSize: 13, color: '#cc2200', background: '#fff0ee', borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>{err}</p>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={submitCustom} disabled={loading}
            style={{ ...s.actionBtn, flex: 1, background: SECTION_COLORS[type].bg, opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Saving…' : 'Add Routine'}
          </button>
          <button onClick={onCancel} style={{ ...s.actionBtn, flex: 1, background: '#eee', color: '#333' }}>Cancel</button>
        </div>
      </div>
    );
  }

  // Preset picker — category drill-down
  const types = Object.keys(PRESET_ROUTINES).filter((t) => PRESET_ROUTINES[t].length > 0);

  if (selectedType) {
    const color = SECTION_COLORS[selectedType];
    const isBreathing = selectedType === 'breathing';
    return (
      <div style={s.formCard}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <button onClick={() => setSelectedType(null)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#888' }}>←</button>
          <h3 style={{ fontWeight: 700, fontSize: 16 }}>{SECTION_ICONS[selectedType]} {selectedType}</h3>
        </div>
        {PRESET_ROUTINES[selectedType].map((preset) => (
          <button key={preset.name}
            onClick={() => isBreathing ? selectBreathingPreset(preset) : selectPreset(preset.name, selectedType, preset.description)}
            disabled={loading}
            style={{ display: 'block', width: '100%', textAlign: 'left', background: color?.light || '#f5f5f5', border: `1.5px solid ${color?.bg || '#ddd'}20`, borderRadius: 10, padding: '10px 14px', marginBottom: 6, cursor: 'pointer', opacity: loading ? 0.6 : 1 }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: color?.bg || '#333' }}>{preset.name}</div>
            {preset.description && (
              <div style={{ fontSize: 12, color: '#777', marginTop: 2 }}>{preset.description}</div>
            )}
            <div style={{ fontSize: 11, color: '#bbb', marginTop: 3 }}>
              {isBreathing
                ? `Inhale ${preset.inhale}${preset.hold > 0 ? ` · Hold ${preset.hold}` : ''} · Exhale ${preset.exhale}${preset.hold2 > 0 ? ` · Hold ${preset.hold2}` : ''}`
                : preset.subroutines.map((sr) => sr.name).join(' · ')}
            </div>
          </button>
        ))}
        {isBreathing && (
          <button onClick={() => { setBreathingName(''); setBreathingInhale(''); setBreathingHold('0'); setBreathingExhale(''); setBreathingHold2('0'); setBreathingDuration(''); setMode('breathing'); }}
            style={{ display: 'block', width: '100%', textAlign: 'left', background: '#f5f5f5', border: '1.5px dashed #ccc', borderRadius: 10, padding: '10px 14px', marginBottom: 6, cursor: 'pointer' }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#555' }}>Custom Breathing</div>
            <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>Set your own cycle pattern and duration</div>
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={s.formCard}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontWeight: 700, fontSize: 16 }}>Add Routine</h3>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#aaa' }}>✕</button>
      </div>

      {types.map((t) => {
        const color = SECTION_COLORS[t];
        return (
          <button key={t} onClick={() => setSelectedType(t)}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', textAlign: 'left', background: color?.light || '#f5f5f5', border: `1.5px solid ${color?.bg || '#ddd'}30`, borderRadius: 12, padding: '14px 16px', marginBottom: 10, cursor: 'pointer' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 22 }}>{SECTION_ICONS[t]}</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: color?.bg || '#333', textTransform: 'capitalize' }}>{t}</div>
                <div style={{ fontSize: 12, color: '#999', marginTop: 1 }}>{PRESET_ROUTINES[t].length} routines</div>
              </div>
            </div>
            <span style={{ color: '#ccc', fontSize: 18 }}>›</span>
          </button>
        );
      })}

      <button onClick={() => setMode('custom')}
        style={{ ...s.actionBtn, width: '100%', background: '#f0f0f0', color: '#555', marginTop: 4 }}>
        + Custom Routine
      </button>
    </div>
  );
}

// ── Bottom Nav ────────────────────────────────────────────────────────────────
function BottomNav({ active, onChange }) {
  const tabs = [
    { key: 'home', icon: '🏠', label: 'Home' },
    { key: 'routines', icon: '📋', label: 'My Routines' },
    { key: 'add', icon: '➕', label: 'Add Routine' },
  ];
  return (
    <nav style={s.bottomNav}>
      {tabs.map((t) => (
        <button key={t.key} onClick={() => onChange(t.key)}
          style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, background: 'none', border: 'none', padding: '10px 0', cursor: 'pointer' }}>
          <span style={{ fontSize: 22 }}>{t.icon}</span>
          <span style={{ fontSize: 11, fontWeight: active === t.key ? 700 : 400, color: active === t.key ? '#ff4d1c' : '#aaa' }}>{t.label}</span>
        </button>
      ))}
    </nav>
  );
}

// ── Streak Calendar Modal ─────────────────────────────────────────────────────
function StreakCalendarModal({ section, logs, onClose }) {
  const color = SECTION_COLORS[section.type] || SECTION_COLORS.custom;
  const [viewDate, setViewDate] = useState(new Date());

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const monthName = viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const nextMonth = () => setViewDate(new Date(year, month + 1, 1));

  const isCompleted = (day) => {
    const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return logs[key]?.done === true;
  };

  const todayKey = new Date().toISOString().slice(0, 10);
  const isToday = (day) => {
    const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return key === todayKey;
  };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', backdropFilter: 'blur(3px)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', width: '100%', maxWidth: 480, borderRadius: '20px 20px 0 0', padding: '24px 20px 32px', borderTop: `5px solid ${color.bg}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 style={{ fontWeight: 700, fontSize: 17, color: '#111' }}>{SECTION_ICONS[section.type]} {section.name}</h3>
            <p style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>Tap to see completed days</p>
          </div>
          <button onClick={onClose} style={{ background: '#f0f0f0', border: 'none', width: 32, height: 32, borderRadius: 8, fontSize: 14, color: '#555', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <button onClick={prevMonth} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888', padding: '0 8px' }}>‹</button>
          <span style={{ fontWeight: 700, fontSize: 15, color: '#111' }}>{monthName}</span>
          <button onClick={nextMonth} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888', padding: '0 8px' }}>›</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 6 }}>
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#bbb', paddingBottom: 4 }}>{d}</div>
          ))}
          {cells.map((day, i) => (
            <div key={i} style={{
              textAlign: 'center', fontSize: 13, fontWeight: day && isCompleted(day) ? 700 : 400,
              background: day && isCompleted(day) ? color.bg : 'transparent',
              color: day && isCompleted(day) ? '#fff' : day && isToday(day) ? color.bg : '#444',
              border: day && isToday(day) && !isCompleted(day) ? `1.5px solid ${color.bg}` : 'none',
              borderRadius: 8, padding: '7px 0', opacity: day ? 1 : 0,
            }}>
              {day || ''}
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#888' }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, background: color.bg }} /> Completed
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#888' }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, border: `1.5px solid ${color.bg}` }} /> Today
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { user, signOut } = useAuth();
  const [sections, setSections] = useState([]);
  const [logs, setLogs] = useState({});
  const [streaks, setStreaks] = useState([]);
  const [activeTab, setActiveTab] = useState('home');
  const [timerRoutine, setTimerRoutine] = useState(null);
  const [calendarSection, setCalendarSection] = useState(null);
  const [loadingData, setLoadingData] = useState(true);

  const displayName = user?.user_metadata?.display_name || user?.email?.split('@')[0] || 'there';

  // Load all sections + today's logs
  const loadData = useCallback(async () => {
    if (!user) return;
    setLoadingData(true);

    const { data: sectionsData } = await supabase
      .from('routines')
      .select('*')
      .order('created_at', { ascending: true });

    const { data: logsData } = await supabase
      .from('logs')
      .select('*')
      .eq('user_id', user.id);

    setSections(sectionsData || []);

    const logsMap = {};
    (logsData || []).forEach((log) => {
      if (!logsMap[log.section_id]) logsMap[log.section_id] = {};
      logsMap[log.section_id][log.date] = { done: log.done, id: log.id };
    });
    setLogs(logsMap);

    // Load streaks and break any that went stale (missed a day)
    const { data: streaksData } = await supabase
      .from('streaks')
      .select('*')
      .eq('user_id', user.id)
      .order('start_date', { ascending: false });

    const yd = yesterday();
    const stale = (streaksData || []).filter(s => s.is_active && s.end_date < yd);
    if (stale.length > 0) {
      await supabase.from('streaks').update({ is_active: false }).in('id', stale.map(s => s.id));
    }
    setStreaks((streaksData || []).map(s => stale.find(ss => ss.id === s.id) ? { ...s, is_active: false } : s));

    setLoadingData(false);
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  const addRoutine = async ({ name, type, description, subroutines }) => {
    const { data, error } = await supabase
      .from('routines')
      .insert({ name, type, user_id: user.id, goal: description ?? null, subroutines: subroutines ?? null })
      .select()
      .single();
    if (!error) { setSections((p) => [...p, data]); setActiveTab('routines'); }
    return error || null;
  };

  const deleteRoutine = async (id) => {
    if (!window.confirm('Delete this routine and all its logs?')) return;
    await supabase.from('routines').delete().eq('id', id);
    setSections((p) => p.filter((x) => x.id !== id));
    setLogs((p) => { const n = { ...p }; delete n[id]; return n; });
    setStreaks((p) => p.filter((s) => s.routine_id !== id));
  };

  const toggleDone = async (sectionId, done) => {
    const dateStr = today();
    const existing = logs[sectionId]?.[dateStr];
    if (existing?.id) {
      await supabase.from('logs').update({ done }).eq('id', existing.id);
    } else {
      const { data } = await supabase.from('logs')
        .upsert({ section_id: sectionId, user_id: user.id, date: dateStr, done }, { onConflict: 'section_id,date' })
        .select().single();
      if (data) {
        setLogs((p) => ({ ...p, [sectionId]: { ...p[sectionId], [dateStr]: { ...p[sectionId]?.[dateStr], done, id: data.id } } }));
        return;
      }
    }
    setLogs((p) => ({ ...p, [sectionId]: { ...p[sectionId], [dateStr]: { ...p[sectionId]?.[dateStr], done } } }));
  };

  const refreshStreaks = async () => {
    const { data } = await supabase
      .from('streaks').select('*').eq('user_id', user.id)
      .order('start_date', { ascending: false });
    setStreaks(data || []);
  };

  const upsertStreak = async (routineId, todayStr, yd) => {
    let q = supabase.from('streaks').select('*').eq('user_id', user.id).eq('is_active', true);
    q = routineId === null ? q.is('routine_id', null) : q.eq('routine_id', routineId);
    const { data: active } = await q.maybeSingle();

    if (active) {
      if (active.end_date === todayStr) return; // already counted today
      if (active.end_date === yd) {
        await supabase.from('streaks').update({ end_date: todayStr, length: active.length + 1 }).eq('id', active.id);
      } else {
        await supabase.from('streaks').update({ is_active: false }).eq('id', active.id);
        await supabase.from('streaks').insert({ user_id: user.id, routine_id: routineId, start_date: todayStr, end_date: todayStr, length: 1, is_active: true });
      }
    } else {
      await supabase.from('streaks').insert({ user_id: user.id, routine_id: routineId, start_date: todayStr, end_date: todayStr, length: 1, is_active: true });
    }
  };

  const updateStreaks = async (sectionId) => {
    const todayStr = today();
    const yd = yesterday();
    if (logs[sectionId]?.[todayStr]?.done) return; // already counted today
    await upsertStreak(sectionId, todayStr, yd);
    const globalAlreadyCounted = sections.some(sec => sec.id !== sectionId && logs[sec.id]?.[todayStr]?.done);
    if (!globalAlreadyCounted) await upsertStreak(null, todayStr, yd);
    await refreshStreaks();
  };

  const doneToday = sections.filter((sec) => logs[sec.id]?.[today()]?.done).length;

  return (
    <div style={s.appBg}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600&display=swap');
        @keyframes fadeUp { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
        * { box-sizing:border-box; margin:0; padding:0; font-family:'DM Sans',sans-serif; }
        input:focus { outline:none; border-color:#ff4d1c !important; box-shadow:0 0 0 3px rgba(255,77,28,0.15); }
        button { cursor:pointer; transition:all 0.15s ease; }
        button:hover { opacity:0.88; }
        button:active { transform:scale(0.96); }
        ::-webkit-scrollbar{width:5px} ::-webkit-scrollbar-thumb{background:#ddd;border-radius:3px}
      `}</style>

      <header style={s.header}>
        <div>
          <h1 style={s.headerTitle}>My Fitness App</h1>
          <p style={s.headerSub}>
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} · Hey, {displayName} 👋
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {activeTab === 'routines' && sections.length > 0 && (
            <div style={s.chip}>
              <span style={{ fontSize: 18, fontFamily: "'Bebas Neue'" }}>{doneToday}/{sections.length}</span>
              <span style={{ fontSize: 11, color: '#888', display: 'block' }}>today</span>
            </div>
          )}
          <button onClick={signOut} style={s.logoutBtn}>Log out</button>
        </div>
      </header>

      <main style={{ ...s.content, paddingBottom: 72 }}>
        {activeTab === 'home' && (() => {
          return (
            <div>
              <div style={{ paddingTop: 8, paddingBottom: 20 }}>
                <h2 style={{ fontFamily: "'Bebas Neue'", fontSize: 26, letterSpacing: 1, color: '#111' }}>Welcome, {displayName}</h2>
                <p style={{ color: '#999', fontSize: 12, marginTop: 2 }}>
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </p>
              </div>

              {/* Per-routine streaks */}
              {sections.length > 0 && (
                <div style={s.card}>
                  <p style={{ ...s.label, marginBottom: 12 }}>📋 Routine Streaks</p>
                  {sections.map(sec => {
                    const color = SECTION_COLORS[sec.type] || SECTION_COLORS.custom;
                    const rs = streaks.filter(s => s.routine_id === sec.id);
                    const cur = rs.find(s => s.is_active);
                    const best = Math.max(0, ...rs.map(s => s.length));
                    return (
                      <div key={sec.id} onClick={() => setCalendarSection(sec)}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #f5f5f5', cursor: 'pointer' }}>
                        <span style={{ fontSize: 20 }}>{SECTION_ICONS[sec.type]}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>{sec.name}</div>
                          <div style={{ fontSize: 12, color: '#aaa' }}>Best: {best}d · tap to view history</div>
                        </div>
                        <div style={{ textAlign: 'center', background: color.light, borderRadius: 8, padding: '4px 14px' }}>
                          <div style={{ fontSize: 22, fontFamily: "'Bebas Neue'", color: color.bg, lineHeight: 1 }}>{cur?.length || 0}</div>
                          <div style={{ fontSize: 10, color: color.bg, fontWeight: 700, letterSpacing: 0.5 }}>DAY{cur?.length !== 1 ? 'S' : ''}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {activeTab === 'routines' && (
          loadingData ? (
            <div style={s.empty}><p style={{ color: '#aaa', fontSize: 15 }}>Loading your data…</p></div>
          ) : (
            <>
              {sections.length === 0 && (
                <div style={s.empty}>
                  <div style={{ fontSize: 48, marginBottom: 12 }}>🏋️</div>
                  <p style={{ fontSize: 18, fontWeight: 600, color: '#333' }}>No routines yet</p>
                  <p style={{ color: '#888', marginTop: 4 }}>Tap Add Routine below to get started.</p>
                </div>
              )}
              {sections.map((sec, i) => (
                <div key={sec.id} style={{ animation: `fadeUp 0.3s ease ${i * 0.05}s both` }}>
                  <RoutineCard
                    section={sec}
                    logs={logs[sec.id] || {}}
                    onDelete={deleteRoutine}
                    onTimer={(sec, preset) => setTimerRoutine({ section: sec, preset })}
                  />
                </div>
              ))}
            </>
          )
        )}

        {activeTab === 'add' && (
          <NewRoutineForm onAdd={addRoutine} onCancel={() => setActiveTab('routines')} />
        )}
      </main>

      <BottomNav active={activeTab} onChange={setActiveTab} />

      {timerRoutine && (
        <TimerModal
          section={timerRoutine.section}
          preset={timerRoutine.preset}
          onMinuteReached={() => { toggleDone(timerRoutine.section.id, true); updateStreaks(timerRoutine.section.id); }}
          onClose={() => setTimerRoutine(null)}
        />
      )}
      {calendarSection && (
        <StreakCalendarModal
          section={calendarSection}
          logs={logs[calendarSection.id] || {}}
          onClose={() => setCalendarSection(null)}
        />
      )}
    </div>
  );
}

const s = {
  appBg: { minHeight: '100vh', background: '#f5f5f3' },
  header: { background: '#fff', borderBottom: '1px solid #eee', padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' },
  headerTitle: { fontFamily: "'Bebas Neue'", fontSize: 28, letterSpacing: 2, color: '#111' },
  headerSub: { fontSize: 12, color: '#999', marginTop: 2 },
  chip: { textAlign: 'center', background: '#f5f5f3', borderRadius: 10, padding: '6px 14px', minWidth: 50 },
  logoutBtn: { fontSize: 13, color: '#888', background: 'none', border: '1px solid #ddd', borderRadius: 8, padding: '6px 12px' },
  content: { maxWidth: 680, margin: '0 auto', padding: '24px 16px' },
  card: { background: '#fff', borderRadius: 14, padding: 18, marginBottom: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' },
  badge: { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, textTransform: 'capitalize' },
  iconBtn: { border: 'none', borderRadius: 8, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, cursor: 'pointer' },
  startBtn: { border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer' },
  checkBtn: { borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  input: { width: '100%', border: '1.5px solid #e5e5e5', borderRadius: 9, padding: '10px 12px', fontSize: 15, color: '#111', background: '#fafafa', marginBottom: 10, display: 'block' },
  label: { fontSize: 12, fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.5 },
  formCard: { background: '#fff', borderRadius: 14, padding: 20, marginBottom: 14, border: '2px dashed #e0e0e0' },
  pill: { border: 'none', borderRadius: 20, padding: '5px 12px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  actionBtn: { border: 'none', borderRadius: 10, padding: '11px 0', fontSize: 14, fontWeight: 700, color: '#fff', cursor: 'pointer' },
  addBtn: { width: '100%', background: '#fff', border: '2px dashed #ddd', borderRadius: 12, padding: '14px 0', fontSize: 14, fontWeight: 600, color: '#888', cursor: 'pointer' },
  empty: { textAlign: 'center', padding: '60px 20px', color: '#666' },
  bottomNav: { position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff', borderTop: '1px solid #eee', display: 'flex', zIndex: 10, boxShadow: '0 -2px 12px rgba(0,0,0,0.04)' },
};
